import HeaderTextButton from "@/components/HeaderTextButton";
import {useMemo} from "react";
import {useQueryModal} from "@/hooks/useQueryModal";
import {useTableQuery} from "@/hooks/useTableQuery";
import {type SortField, type TeamMember, useDeleteTeamMember, useTeamMembers,} from "@/hooks/useTeamMembers";
import TeamMemberTable from "@/features/team/components/TeamMemberTable.tsx";
import TeamMemberUpsertDialog from "@/features/team/components/TeamMemberUpsertDialog.tsx";

export default function AdminTeamMembersPage() {
    const {
        searchParams,
        editId,
        isNew,
        modalOpen,
        setParam,
        openCreate,
        openEdit,
        closeModal,
    } = useQueryModal();

    const {
        page,
        size,
        sortBy,
        sortOrder,
        setPage,
        onRequestSort,
    } = useTableQuery<SortField>({
        searchParams,
        setParam,
        defaultPage: 0,
        defaultSize: 10,
        defaultSortBy: "name",
        defaultSortOrder: "asc",
    });

    const {
        data: result,
        isLoading,
        isError,
    } = useTeamMembers({page, size, sortBy, sortOrder});

    const {mutateAsync: deleteTeamMember} = useDeleteTeamMember();

    const initialForDialog: Partial<TeamMember> | null = useMemo(() => {
        if (isNew) return null;
        if (editId) return result.items.find((m: TeamMember) => m.id === editId) ?? null;
        return null;
    }, [isNew, editId, result]);

    return (
        <>
            <HeaderTextButton
                title="Team Members"
                description="Manage your team profiles shown on the site."
                label="Add Team Member"
                onClick={openCreate}
            />

            <TeamMemberTable
                content={result}
                isLoading={isLoading}
                isError={isError}
                onCreate={openCreate}
                onEdit={openEdit}
                onDelete={deleteTeamMember}
                setPage={setPage}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onRequestSort={onRequestSort}
            />

            <TeamMemberUpsertDialog open={modalOpen} initial={initialForDialog} onClose={closeModal}/>
        </>
    );
}
